import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { PaginatedResponse } from 'app/_models/paginated-data.model';
import { WorkflowItemFactory } from 'app/_models/workflow-items/workflow-item.factory';
import { WorkflowItem } from 'app/_models/workflow-items/workflow-item.model';
import { environment } from 'environments/environment';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { BaseHttpClientService } from './base-http-client.service';
import { Pagination } from 'app/_models/pagination.model';
import { QueryStringSearch } from 'app/_models/query-string-search.model';
import { WorkflowAccessService } from './workflow-access.service';

/**
 * The Workflow Approvals list filters. All optional and combined with AND by the API.
 *
 * Dates are ISO instants ending in Z
 */
export interface WorkflowItemFilters {
  siteId?: number;
  textFilter?: string;
  fromDate?: string;
  toDate?: string;
}

@Injectable()
export class WorkflowItemService extends BaseHttpClientService {
  private readonly workerCompetencyCategory = 'worker-competency';
  private readonly workerCompetencyWorkflowType = 'WORKER_COMPETENCY';
  private workflowItemCache: { [cacheKey: string]: WorkflowItem } = {};

  constructor(private http: HttpClient, private workflowAccessService: WorkflowAccessService) {
    super()
  }

  public getPendingWorkflowItemsNoPagination(): Observable<WorkflowItem[]> {
    const pagination = {
      size: 1000,
      page: 0,
      totalRecords: 0,
    };
    let params = this.createQueryStrings(pagination, ['lastModifiedDate,desc']);
    params = params.append('statusList', 'PENDING');
    return (this.http.get(environment.apiUrl + 'api/ext-workflow-item/by-category/contractor-cert/list', { params, observe: 'response' }) as any)
      .pipe(map((response) => this.handlePaginatedData(response as HttpResponse<any>)))
      .pipe(map(paginationResponse => (paginationResponse as any).body.map((item: any) => WorkflowItemFactory.create(item))))
      .pipe(catchError(this.handleError));
  }

  public getWorkflowItemsByCategory(
    category: string,
    statusList: string,
    pagination: Pagination,
    filters: WorkflowItemFilters = {},
  ): Observable<PaginatedResponse<WorkflowItem>> {
    if (this.isWorkerCompetency(category) && !this.workflowAccessService.canViewWorkerCompetencyWorkflow()) {
      return of({ totalRecords: 0, body: [] });
    }

    if (this.isWorkerCompetency(category)) {
      return this.getWorkerCompetencyApprovalItems(statusList, pagination, filters);
    }

    let params = this.createQueryStrings(pagination, ['lastModifiedDate,desc'], this.toSearchParams(filters, true));
    params = params.append('statusList', statusList);

    return (this.http.get(environment.apiUrl + `api/ext-workflow-item/by-category/${category}/list`, { params, observe: 'response' }) as any)
      .pipe(map((response) => this.handlePaginatedData(response as HttpResponse<any>)))
      .pipe(map(paginationResponse => {
        (paginationResponse as any).body = (paginationResponse as any).body.map((item: any) => WorkflowItemFactory.create(item));
        return paginationResponse;
      }))
      .pipe(catchError(this.handleError));
  }

  public getPendingWorkflowItemsCountByCategory(category: string): Observable<number> {
    return this.getPendingWorkflowCount(category).pipe(catchError((_err) => of(0)));
  }

  public getPendingWorkflowTotalCount(): Observable<number> {
    return this.getPendingWorkflowCount().pipe(catchError((_err) => of(0)));
  }

  public getPendingWorkflowCount(category?: string): Observable<number> {
    if (this.isWorkerCompetency(category) && !this.workflowAccessService.canViewWorkerCompetencyWorkflow()) {
      return of(0);
    }

    let params = new HttpParams();

    if (category) {
      params = params.set('category', category);
    }

    return this.http
      .get<number>(environment.apiUrl + `api/ext-workflow-item/pending-count`, { params })
      .pipe(catchError(this.handleError));
  }

  public getPendingWorkflowCounts(categories: string[] = []): Observable<{ totalRecords: number; byCategory: { [category: string]: number } }> {
    const categoryRequests = categories.map((category) => this.getPendingWorkflowCount(category));

    if (!categoryRequests.length) {
      return of({ totalRecords: 0, byCategory: {} });
    }

    return forkJoin(categoryRequests).pipe(
      map((categoryCounts) => {
        const byCategory: { [category: string]: number } = {};

        categories.forEach((category, index) => {
          byCategory[category] = categoryCounts[index] || 0;
        });

        return {
          totalRecords: categoryCounts.reduce((acc, count) => acc + (count || 0), 0),
          byCategory,
        };
      }),
      catchError(this.handleError)
    );
  }

  private getWorkerCompetencyApprovalItems(
    statusList: string,
    pagination: Pagination,
    filters: WorkflowItemFilters = {},
  ): Observable<PaginatedResponse<WorkflowItem>> {
    // siteId means something narrower here than on the other tabs: an approval has no site of its
    // own, so it selects approvals for workers assigned to that site, issued by that site's owner.
    let params = this.createQueryStrings(pagination, ['lastModifiedDate,desc'], this.toSearchParams(filters, true));
    params = params.append('statusList', this.getStatusListForCategory(this.workerCompetencyCategory, statusList));

    return this.http
      .get<any[]>(environment.competencyApiUrl + `api/worker-competency/approvals`, { params, observe: 'response' })
      .pipe(map((response: HttpResponse<any[]>) => {
        const body = Array.isArray(response.body) ? response.body : [];
        const totalRecordsHeader = response.headers ? response.headers.get('X-Total-Count') : null;
        const totalRecords = totalRecordsHeader != null ? Number(totalRecordsHeader) : body.length;

        return {
          totalRecords: isNaN(totalRecords) ? body.length : totalRecords,
          body: body.map((item: any) => WorkflowItemFactory.create(this.toWorkerCompetencyWorkflowItem(item))),
        };
      }))
      .pipe(catchError(this.handleError));
  }

  public getWorkflowItemById(id): Observable<WorkflowItem> {
    return (this.http.get(environment.apiUrl + `api/ext-workflow-item/${id}`) as any)
      .pipe(map(response => WorkflowItemFactory.create(response)))
      .pipe(catchError(this.handleError));
  }

  // Stores the workflow item loaded from the inbox so the approval page can reuse it without a second request.
  public cacheWorkflowItem(workflowItem: WorkflowItem, workflowType?: string) {
    if (!workflowItem || workflowItem.id == null) {
      return;
    }

    const cacheKey = this.getWorkflowItemCacheKey(workflowItem.id, workflowType || workflowItem.workflowType);
    this.workflowItemCache[cacheKey] = workflowItem;
  }

  // Reads the cached workflow item for the current approval flow, if it exists.
  public getCachedWorkflowItem(id: any, workflowType?: string): WorkflowItem | null {
    const cacheKey = this.getWorkflowItemCacheKey(id, workflowType);
    return this.workflowItemCache[cacheKey] || null;
  }

  // Clears the cached workflow item after the approval page consumes it.
  public clearCachedWorkflowItem(id: any, workflowType?: string) {
    const cacheKey = this.getWorkflowItemCacheKey(id, workflowType);
    delete this.workflowItemCache[cacheKey];
  }

  public getPendingWorkflowItemsForWorker(filter) {
    const pagination = {
      size: 1000,
      page: 0,
      totalRecords: 0,
    };
    let params = this.createQueryStrings(pagination, ["startTime,desc"], filter);
    params = params.append("statusList", "PENDING");
    return (this.http.get(environment.apiUrl + `api/ext-workflow-item/current-worker`, { params }) as any)
      .pipe(map(response => (response as any).map((item: any) => WorkflowItemFactory.create(item))))
      .pipe(catchError(this.handleError));
  }

  public approveOrRejectItem(workflowItemId, body, workflowType?: string) {
    if (this.isWorkerCompetency(workflowType)) {
      if (!this.workflowAccessService.canViewWorkerCompetencyWorkflow()) {
        return of(null);
      }

      const bodyData = body || {};
      const formData = new FormData();
      const statusUpdate: any = {
        siteOwnerSkylarkUserId: bodyData.siteOwnerSkylarkUserId,
        status: bodyData.status,
      };

      if (bodyData.expiryDate != null) {
        statusUpdate.expiryDate = bodyData.expiryDate;
      }

      if (bodyData.reason != null) {
        statusUpdate.reason = bodyData.reason;
      }

      formData.append('statusUpdate', new Blob([JSON.stringify(statusUpdate)], { type: 'application/json' }));

      if (bodyData.file) {
        formData.append('file', bodyData.file, bodyData.file.name);
      }

      return (this.http.put(this.getWorkerCompetencyApprovalUrl(workflowItemId), formData) as any)
        .pipe(catchError(this.handleError));
    }

    return (this.http.put(environment.apiUrl + `api/ext-workflow-item/${workflowItemId}`, body) as any)
      .pipe(catchError(this.handleError));
  }

  public createWorkflowItem(body) {
    return this.http
      .post(environment.apiUrl + 'api/ext-workflow-item', body)
      .pipe(catchError(this.handleError));
  }

  public updateAssetAvailaibityWorkflowItem(workflowItemId, body) {
    return this.http
      .put(environment.apiUrl + `api/ext-workflow-item/${workflowItemId}/current-worker`, body)
      .pipe(catchError(this.handleError));
  }

  public deleteWorkflowItem(workflowItemId) {
    return this.http
      .delete(environment.apiUrl + `api/ext-workflow-item/${workflowItemId}/current-worker`)
      .pipe(catchError(this.handleError));
  }

  public getRejectedWorkflowItems(filter) {
    const pagination = {
      size: 1000,
      page: 0,
      totalRecords: 0,
    };
    let params = this.createQueryStrings(pagination, ["startTime,desc"], filter);
    params = params.append("statusList", "REJECTED");
    return (this.http.get(environment.apiUrl + `api/ext-workflow-item/current-worker`, { params }) as any)
      .pipe(map(response => (response as any).map((item: any) => WorkflowItemFactory.create(item))))
      .pipe(catchError(this.handleError));
  }

  private getWorkerCompetencyApprovalUrl(workflowItemId: any): string {
    return `${environment.competencyApiUrl}api/worker-competency/${workflowItemId}/approval`;
  }

  private getStatusListForCategory(category: string, statusList: string): string {
    if (!this.isWorkerCompetency(category)) {
      return statusList;
    }

    switch (statusList) {
      case 'PENDING':
        return 'DOCUMENT_SUBMITTED';
      case 'APPROVED,REJECTED':
        return 'APPROVED,DOCUMENT_REJECTED';
      default:
        return statusList;
    }
  }

  private toSearchParams(filters: WorkflowItemFilters, includeSiteId: boolean): QueryStringSearch[] {
    const search: QueryStringSearch[] = [];

    if (includeSiteId && filters.siteId != null) {
      search.push({ key: 'siteId', value: String(filters.siteId) });
    }

    search.push({ key: 'textFilter', value: filters.textFilter || '' });
    search.push({ key: 'fromDate', value: filters.fromDate || '' });
    search.push({ key: 'toDate', value: filters.toDate || '' });

    return search;
  }

  private isWorkerCompetency(categoryOrWorkflowType?: string): boolean {
    return categoryOrWorkflowType === this.workerCompetencyCategory
      || categoryOrWorkflowType === this.workerCompetencyWorkflowType;
  }

  // Builds a stable cache key so worker competency and other workflow types do not collide.
  private getWorkflowItemCacheKey(id: any, workflowType?: string): string {
    return `${workflowType || 'DEFAULT'}:${String(id)}`;
  }

  private toWorkerCompetencyWorkflowItem(item: any): any {
    return Object.assign({}, item, {
      workflowType: this.workerCompetencyWorkflowType,
      id: item.competencyApprovalId,
      createdDate: item.approvalCreatedDate,
      lastModifiedDate: item.approvalLastModifiedDate,
      createdBy: item.approvalCreatedBy,
      status: item.status,
    });
  }

}
